import { inject } from '@angular/core';
import { PAGINATION_DEFAULT } from '@core/constants';
import {
  AuditLogEntry,
  AuditLogResponseModel,
  InstallationGroupDetail,
  InstallationGroupFilterModel,
  InstallationGroupInstallationsResponseModel,
  InstallationGroupMemberDevicesResponseModel,
  InstallationGroupMemberLocationsResponseModel,
  InstallationGroupsResponseModel,
  InstallationGroupWithAccess,
  NewInstallationGroup,
  UpdateInstallationGroup,
} from '@core/models';
import { AuditLogService, InstallationGroupService, PartnershipService } from '@core/services';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, forkJoin, map, of, pipe, switchMap, tap } from 'rxjs';

type InstallationGroupState = {
  isLoading: boolean;
  data: InstallationGroupsResponseModel;
  filter: InstallationGroupFilterModel;
  isLoadingDetail: boolean;
  detail: InstallationGroupDetail | undefined;
  devices: InstallationGroupMemberDevicesResponseModel;
  isLoadingDevices: boolean;
  locations: InstallationGroupMemberLocationsResponseModel;
  isLoadingLocations: boolean;
  installations: InstallationGroupInstallationsResponseModel;
  isLoadingInstallations: boolean;
  isLoadingAuditLog: boolean;
  auditLog: AuditLogEntry[];
  auditLogCount: number;
};

const initialState: InstallationGroupState = {
  isLoading: false,
  data: { numberOfItems: 0, installationGroups: [] },
  filter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
  },
  isLoadingDetail: false,
  detail: undefined,
  devices: { numberOfItems: 0, devices: [] },
  isLoadingDevices: false,
  locations: { numberOfItems: 0, locations: [] },
  isLoadingLocations: false,
  installations: { numberOfItems: 0, installations: [] },
  isLoadingInstallations: false,
  isLoadingAuditLog: false,
  auditLog: [],
  auditLogCount: 0,
};

export const InstallationGroupStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      installationGroupService = inject(InstallationGroupService),
      auditLogService = inject(AuditLogService),
      partnershipService = inject(PartnershipService),
    ) => ({
      getInstallationGroups: rxMethod<InstallationGroupFilterModel>(
        pipe(
          tap(filter => patchState(store, { isLoading: true, filter })),
          switchMap(filter =>
            installationGroupService.getInstallationGroups(filter).pipe(
              tapResponse({
                next: (data: InstallationGroupsResponseModel) => {
                  patchState(store, { data, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            ),
          ),
        ),
      ),

      createInstallationGroup: rxMethod<{
        partnerOid: string;
        name: string;
        description?: string;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ partnerOid, name, description }) => {
            const group: NewInstallationGroup = { name, description };
            return installationGroupService.createInstallationGroup(partnerOid, group).pipe(
              switchMap(() => installationGroupService.getInstallationGroups(store.filter())),
              tapResponse({
                next: (data: InstallationGroupsResponseModel) => {
                  patchState(store, { data, isLoading: false });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),

      getInstallationGroup: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoadingDetail: true, detail: undefined })),
          switchMap(groupOid =>
            installationGroupService.getInstallationGroup(groupOid).pipe(
              tapResponse({
                next: (detail: InstallationGroupDetail) => {
                  patchState(store, { detail, isLoadingDetail: false });
                },
                error: () => {
                  patchState(store, { isLoadingDetail: false });
                },
              }),
            ),
          ),
        ),
      ),

      updateInstallationGroup: rxMethod<{ groupOid: string; changes: UpdateInstallationGroup }>(
        pipe(
          tap(() => patchState(store, { isLoadingDetail: true })),
          switchMap(({ groupOid, changes }) =>
            installationGroupService.updateInstallationGroup(groupOid, changes).pipe(
              tapResponse({
                next: (updated: InstallationGroupWithAccess) => {
                  const current = store.detail();
                  // The update endpoint returns the base group without access metadata, so
                  // merge the edited fields onto the existing detail to preserve accessVia etc.
                  patchState(store, {
                    detail: current
                      ? {
                          ...current,
                          name: updated.name,
                          description: updated.description,
                          status: updated.status,
                        }
                      : current,
                    isLoadingDetail: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingDetail: false });
                },
              }),
            ),
          ),
        ),
      ),

      getInstallationGroupDevices: rxMethod<{ groupOid: string; limit?: number; offset?: number }>(
        pipe(
          tap(() => patchState(store, { isLoadingDevices: true })),
          switchMap(({ groupOid, limit, offset }) =>
            installationGroupService.getInstallationGroupDevices(groupOid, limit, offset).pipe(
              tapResponse({
                next: (devices: InstallationGroupMemberDevicesResponseModel) => {
                  patchState(store, { devices, isLoadingDevices: false });
                },
                error: () => {
                  patchState(store, { isLoadingDevices: false });
                },
              }),
            ),
          ),
        ),
      ),

      getInstallationGroupLocations: rxMethod<{
        groupOid: string;
        limit?: number;
        offset?: number;
      }>(
        pipe(
          tap(() => patchState(store, { isLoadingLocations: true })),
          switchMap(({ groupOid, limit, offset }) =>
            installationGroupService.getInstallationGroupLocations(groupOid, limit, offset).pipe(
              tapResponse({
                next: (locations: InstallationGroupMemberLocationsResponseModel) => {
                  patchState(store, { locations, isLoadingLocations: false });
                },
                error: () => {
                  patchState(store, { isLoadingLocations: false });
                },
              }),
            ),
          ),
        ),
      ),

      getInstallationGroupInstallations: rxMethod<{
        groupOid: string;
        limit?: number;
        offset?: number;
      }>(
        pipe(
          tap(() => patchState(store, { isLoadingInstallations: true })),
          switchMap(({ groupOid, limit, offset }) =>
            installationGroupService
              .getInstallationGroupInstallations(groupOid, limit, offset)
              .pipe(
                tapResponse({
                  next: (installations: InstallationGroupInstallationsResponseModel) => {
                    patchState(store, { installations, isLoadingInstallations: false });
                  },
                  // Emptied so a failed read never shows the rows of the group opened before.
                  error: () => {
                    patchState(store, {
                      installations: { numberOfItems: 0, installations: [] },
                      isLoadingInstallations: false,
                    });
                  },
                }),
              ),
          ),
        ),
      ),

      getAuditLog: rxMethod<{ installationGroupOid: string; limit?: number; offset?: number }>(
        pipe(
          tap(() => patchState(store, { isLoadingAuditLog: true })),
          switchMap(({ installationGroupOid, limit, offset }) =>
            auditLogService.listAuditLog({ installationGroupOid, limit, offset }).pipe(
              tapResponse({
                next: (data: AuditLogResponseModel) => {
                  patchState(store, {
                    auditLog: data.entries,
                    auditLogCount: data.numberOfItems,
                    isLoadingAuditLog: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoadingAuditLog: false });
                },
              }),
            ),
          ),
        ),
      ),

      addDeviceToGroup: rxMethod<{ groupOid: string; deviceId: number }>(
        pipe(
          switchMap(({ groupOid, deviceId }) =>
            installationGroupService.addDeviceToGroup(groupOid, deviceId).pipe(
              switchMap(() =>
                installationGroupService.getInstallationGroupDevices(
                  groupOid,
                  PAGINATION_DEFAULT.limit,
                ),
              ),
              tapResponse({
                next: (devices: InstallationGroupMemberDevicesResponseModel) => {
                  patchState(store, { devices });
                },
                error: () => {},
              }),
            ),
          ),
        ),
      ),

      addLocationToGroup: rxMethod<{ groupOid: string; locationId: number }>(
        pipe(
          switchMap(({ groupOid, locationId }) =>
            installationGroupService.addLocationToGroup(groupOid, locationId).pipe(
              switchMap(() =>
                installationGroupService.getInstallationGroupLocations(
                  groupOid,
                  PAGINATION_DEFAULT.limit,
                ),
              ),
              tapResponse({
                next: (locations: InstallationGroupMemberLocationsResponseModel) => {
                  patchState(store, { locations });
                },
                error: () => {},
              }),
            ),
          ),
        ),
      ),

      /**
       * Add many locations to the group in one go. Each add fires independently (client-side
       * fan-out) and never aborts the batch — a failed add resolves to `false` instead of
       * erroring the whole stream (the HTTP error interceptor surfaces it). Once all settle the
       * locations list is refetched a single time from the first page.
       */
      addLocationsToGroup: rxMethod<{ groupOid: string; locationIds: number[] }>(
        pipe(
          tap(() => patchState(store, { isLoadingLocations: true })),
          switchMap(({ groupOid, locationIds }) =>
            forkJoin(
              locationIds.map(locationId =>
                installationGroupService.addLocationToGroup(groupOid, locationId).pipe(
                  map(() => true),
                  catchError(() => of(false)),
                ),
              ),
            ).pipe(
              switchMap(() =>
                installationGroupService.getInstallationGroupLocations(
                  groupOid,
                  PAGINATION_DEFAULT.limit,
                ),
              ),
              tapResponse({
                next: (locations: InstallationGroupMemberLocationsResponseModel) => {
                  patchState(store, { locations, isLoadingLocations: false });
                },
                error: () => {
                  patchState(store, { isLoadingLocations: false });
                },
              }),
            ),
          ),
        ),
      ),

      /**
       * Add many devices to the group in one go. Each add fires independently (client-side
       * fan-out) and never aborts the batch — a failed add resolves to `false` instead of
       * erroring the whole stream (the HTTP error interceptor surfaces it). Once all settle the
       * devices list is refetched a single time from the first page.
       */
      addDevicesToGroup: rxMethod<{ groupOid: string; deviceIds: number[] }>(
        pipe(
          tap(() => patchState(store, { isLoadingDevices: true })),
          switchMap(({ groupOid, deviceIds }) =>
            forkJoin(
              deviceIds.map(deviceId =>
                installationGroupService.addDeviceToGroup(groupOid, deviceId).pipe(
                  map(() => true),
                  catchError(() => of(false)),
                ),
              ),
            ).pipe(
              switchMap(() =>
                installationGroupService.getInstallationGroupDevices(
                  groupOid,
                  PAGINATION_DEFAULT.limit,
                ),
              ),
              tapResponse({
                next: (devices: InstallationGroupMemberDevicesResponseModel) => {
                  patchState(store, { devices, isLoadingDevices: false });
                },
                error: () => {
                  patchState(store, { isLoadingDevices: false });
                },
              }),
            ),
          ),
        ),
      ),

      removeDeviceFromGroup: rxMethod<{ groupOid: string; deviceId: number; offset?: number }>(
        pipe(
          switchMap(({ groupOid, deviceId, offset }) =>
            installationGroupService.removeDeviceFromGroup(groupOid, deviceId).pipe(
              switchMap(() =>
                installationGroupService.getInstallationGroupDevices(
                  groupOid,
                  PAGINATION_DEFAULT.limit,
                  offset,
                ),
              ),
              tapResponse({
                next: (devices: InstallationGroupMemberDevicesResponseModel) => {
                  patchState(store, { devices });
                },
                error: () => {},
              }),
            ),
          ),
        ),
      ),

      removeLocationFromGroup: rxMethod<{ groupOid: string; locationId: number; offset?: number }>(
        pipe(
          switchMap(({ groupOid, locationId, offset }) =>
            installationGroupService.removeLocationFromGroup(groupOid, locationId).pipe(
              switchMap(() =>
                installationGroupService.getInstallationGroupLocations(
                  groupOid,
                  PAGINATION_DEFAULT.limit,
                  offset,
                ),
              ),
              tapResponse({
                next: (locations: InstallationGroupMemberLocationsResponseModel) => {
                  patchState(store, { locations });
                },
                error: () => {},
              }),
            ),
          ),
        ),
      ),

      addGroupToPartnership: rxMethod<{ groupOid: string; partnershipOid: string }>(
        pipe(
          switchMap(({ groupOid, partnershipOid }) =>
            partnershipService.addInstallationGroupToPartnership(partnershipOid, groupOid).pipe(
              // Re-fetch the detail (without the loading reset) so the partnerships list updates.
              switchMap(() => installationGroupService.getInstallationGroup(groupOid)),
              tapResponse({
                next: (detail: InstallationGroupDetail) => {
                  patchState(store, { detail });
                },
                error: () => {},
              }),
            ),
          ),
        ),
      ),

      removeGroupFromPartnership: rxMethod<{ groupOid: string; partnershipOid: string }>(
        pipe(
          switchMap(({ groupOid, partnershipOid }) =>
            partnershipService
              .removeInstallationGroupFromPartnership(partnershipOid, groupOid)
              .pipe(
                switchMap(() => installationGroupService.getInstallationGroup(groupOid)),
                tapResponse({
                  next: (detail: InstallationGroupDetail) => {
                    patchState(store, { detail });
                  },
                  error: () => {},
                }),
              ),
          ),
        ),
      ),
    }),
  ),
);
